import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, Loader2 } from 'lucide-react';
import {
  GlassCard,
  GlassButton,
  GlassInput,
  GlassSelect,
} from '@/design-system';
import type { RootState, AppDispatch } from '@/store';
import { fetchPrograms } from '@/store/slices/programsSlice';
import { fetchScenarios } from '@/store/slices/scenariosSlice';
import {
  createFeatureAllocation,
  updateFeatureAllocation,
  type FeatureAllocation,
} from '@/store/slices/featureAllocationsSlice';

interface FeatureAllocationFormProps {
  featureId: string;
  allocation?: FeatureAllocation | null;
  onClose: () => void;
  onSuccess: () => void;
}

const FeatureAllocationForm: React.FC<FeatureAllocationFormProps> = ({
  featureId,
  allocation,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const isEditing = !!allocation;

  // Form state
  const [programId, setProgramId] = useState(allocation?.programId || '');
  const [teamWeeks, setTeamWeeks] = useState(allocation?.teamWeeks?.toString() || '');
  const [scenarioId, setScenarioId] = useState(allocation?.scenarioId || '');
  const [quarter, setQuarter] = useState(allocation?.quarter?.toString() || '');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Selectors
  const programs = useSelector((state: RootState) => state.programs.programs);
  const programsLoading = useSelector((state: RootState) => state.programs.isLoading);
  const scenarios = useSelector((state: RootState) => state.scenarios.scenarios);
  const scenariosLoading = useSelector((state: RootState) => state.scenarios.isLoading);

  // Fetch programs and scenarios on mount
  useEffect(() => {
    if (programs.length === 0) {
      dispatch(fetchPrograms({}));
    }
    if (scenarios.length === 0) {
      dispatch(fetchScenarios({}));
    }
  }, [dispatch, programs.length, scenarios.length]);

  // Convert programs to select options
  const programOptions = useMemo(() => {
    return programs.map((program) => ({
      value: program.id,
      label: program.name,
    }));
  }, [programs]);

  // Convert scenarios to select options
  const scenarioOptions = useMemo(() => {
    return [
      { value: '', label: 'No Scenario' },
      ...scenarios.map((scenario) => ({
        value: scenario.id,
        label: scenario.name,
      })),
    ];
  }, [scenarios]);

  // Quarter options
  const quarterOptions = useMemo(() => [
    { value: '', label: 'Select Quarter (Optional)' },
    { value: '1', label: 'Q1' },
    { value: '2', label: 'Q2' },
    { value: '3', label: 'Q3' },
    { value: '4', label: 'Q4' },
  ], []);

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!programId) {
      setError('Please select a program');
      return;
    }
    const twValue = parseFloat(teamWeeks);
    if (isNaN(twValue) || twValue <= 0) {
      setError('Team Weeks must be a positive number');
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditing && allocation) {
        await dispatch(
          updateFeatureAllocation({
            id: allocation.id,
            featureId,
            data: {
              teamWeeks: twValue,
              scenarioId: scenarioId || undefined,
              quarter: quarter ? parseInt(quarter) : undefined,
            },
          })
        ).unwrap();
      } else {
        await dispatch(
          createFeatureAllocation({
            featureId,
            programId,
            teamWeeks: twValue,
            scenarioId: scenarioId || undefined,
            quarter: quarter ? parseInt(quarter) : undefined,
          })
        ).unwrap();
      }
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to save allocation');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <GlassCard
        variant="strong"
        padding="lg"
        className="relative z-10 w-full max-w-md mx-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">
            {isEditing ? 'Edit Allocation' : 'Add Allocation'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Close allocation form"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Program Select */}
          <GlassSelect
            label="Program"
            value={programId}
            onChange={setProgramId}
            options={programOptions}
            placeholder="Select Program"
            disabled={isEditing || programsLoading}
            fullWidth
          />

          {/* Team Weeks */}
          <GlassInput
            label="Team Weeks"
            type="number"
            value={teamWeeks}
            onChange={setTeamWeeks}
            placeholder="Enter team weeks"
          />

          {/* Scenario Select (Optional) */}
          <GlassSelect
            label="Scenario (Optional)"
            value={scenarioId}
            onChange={setScenarioId}
            options={scenarioOptions}
            disabled={scenariosLoading}
            fullWidth
          />

          {/* Quarter Select (Optional) */}
          <GlassSelect
            label="Quarter (Optional)"
            value={quarter}
            onChange={setQuarter}
            options={quarterOptions}
            fullWidth
          />

          {/* Error Message */}
          {error && (
            <div className="p-3 rounded-lg bg-accent-rose/10 border border-accent-rose/20">
              <p className="text-sm text-accent-rose">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <GlassButton
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </GlassButton>
            <GlassButton
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              leftIcon={isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined}
            >
              {isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Add Allocation'}
            </GlassButton>
          </div>
        </form>
      </GlassCard>
    </div>
  );
};

export default FeatureAllocationForm;
